import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Alert,
  Chip,
  Toolbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';
import PageLoader from '../components/PageLoader';
import { useAuth } from '../context/AuthContext';

const UserManagement = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role_id: ''
  });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    id: null,
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const [page, setPage] = useState(0);          // MUI is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const searchDebounceRef = useRef(null);

  const fetchUsers = useCallback(async (currentPage = page, currentSearch = searchTerm, currentRowsPerPage = rowsPerPage) => {
    try {
      setLoading(true);
      const params = { page: currentPage + 1, page_size: currentRowsPerPage };
      if (currentSearch.trim()) params.search = currentSearch.trim();
      const response = await apiClient.get('/users/', { params });
      const data = response.data;
      setUsers(data.results || data);
      setTotalCount(data.count || (data.results ? data.results.length : data.length));
      setError('');
    } catch (err) {
      setError(t('userManagement.errorLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]); // otherwise stable — always called with explicit args

  useEffect(() => {
    fetchUsers(0, '', rowsPerPage);
    fetchRoles();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRoles = async () => {
    try {
      const response = await apiClient.get('/roles/');
      setRoles(response.data.results || response.data);
    } catch (err) {
      // non-critical
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPage(0);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchUsers(0, value, rowsPerPage);
    }, 400);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    fetchUsers(newPage, searchTerm, rowsPerPage);
  };

  const handleRowsPerPageChange = (e) => {
    const newRowsPerPage = parseInt(e.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchUsers(0, searchTerm, newRowsPerPage);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await apiClient.post('/users/create_user/', formData);
      setSuccess(t('userManagement.successUserCreated'));
      setShowModal(false);
      setShowPassword(false);
      setFormData({
        username: '',
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role_id: ''
      });
      fetchUsers(page, searchTerm, rowsPerPage);
    } catch (err) {
      setError(err.response?.data?.error || t('userManagement.errorCreateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (user) => {
    setEditFormData({
      id: user.id,
      username: user.username,
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      role_id: user.role_id || ''
    });
    setError('');
    setEditModalOpen(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await apiClient.patch(`/users/${editFormData.id}/`, {
        email: editFormData.email,
        first_name: editFormData.first_name,
        last_name: editFormData.last_name,
      });
      if (editFormData.role_id) {
        await apiClient.post(`/users/${editFormData.id}/assign_role/`, { role_id: editFormData.role_id });
      }
      setSuccess(t('userManagement.successUserUpdated'));
      setEditModalOpen(false);
      fetchUsers(page, searchTerm, rowsPerPage);
    } catch (err) {
      setError(err.response?.data?.error || t('userManagement.errorUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async (userId, roleId) => {
    try {
      setLoading(true);
      await apiClient.post(`/users/${userId}/assign_role/`, { role_id: roleId });
      setSuccess(t('userManagement.successRoleAssigned'));
      fetchUsers(page, searchTerm, rowsPerPage);
    } catch (err) {
      setError(t('userManagement.errorAssignFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (window.confirm(t('userManagement.confirmDeactivate'))) {
      try {
        setLoading(true);
        await apiClient.post(`/users/${userId}/deactivate/`);
        setSuccess(t('userManagement.successDeactivated'));
        fetchUsers(page, searchTerm, rowsPerPage);
      } catch (err) {
        setError(t('userManagement.errorDeactivateFailed'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      setLoading(true);
      await apiClient.post(`/users/${userId}/activate/`);
      setSuccess(t('userManagement.successActivated'));
      fetchUsers(page, searchTerm, rowsPerPage);
    } catch (err) {
      setError(t('userManagement.errorActivateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirmUser) return;
    try {
      setLoading(true);
      await apiClient.delete(`/users/${deleteConfirmUser.id}/`);
      setSuccess(t('userManagement.successDeleted', { username: deleteConfirmUser.username }));
      setDeleteConfirmUser(null);
      fetchUsers(page, searchTerm, rowsPerPage);
    } catch (err) {
      setError(err.response?.data?.error || t('userManagement.errorDeleteFailed'));
      setDeleteConfirmUser(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Toolbar sx={{ pl: 0, pr: 0, mb: 3, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
          {t('userManagement.pageTitle')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setFormData({ username: '', email: '', password: '', first_name: '', last_name: '', role_id: '' });
            setShowPassword(false);
            setShowModal(true);
          }}
        >
          {t('userManagement.addNewUser')}
        </Button>
      </Toolbar>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder={t('userManagement.searchPlaceholder')}
          value={searchTerm}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          sx={{ maxWidth: 400 }}
        />
      </Box>

      {loading && <PageLoader variant="table" rows={5} />}

      {!loading && (
        <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 1, mb: 4, maxWidth: '100%', overflowX: 'auto' }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', padding: '16px' }}>{t('register.username')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', padding: '16px' }}>{t('register.email')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', padding: '16px' }}>{t('userManagement.fullName')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', padding: '16px' }}>{t('userManagement.role')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', padding: '16px' }}>{t('permitList.status')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', padding: '16px' }}>{t('permitList.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length > 0 ? (
                users.map(user => (
                  <TableRow
                    key={user.id}
                    sx={{
                      backgroundColor: !user.is_active ? '#f0f0f0' : 'inherit',
                      '&:hover': {
                        backgroundColor: '#f9f9f9',
                      },
                    }}
                  >
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.first_name} {user.last_name}</TableCell>
                    <TableCell>
                      {user.role_name ? (
                        <Chip
                          label={user.role_name}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ) : (
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <Select
                            value=""
                            displayEmpty
                            onChange={(e) => handleAssignRole(user.id, e.target.value)}
                          >
                            <MenuItem value="" disabled>{t('userManagement.assignRole')}</MenuItem>
                            {roles.map(role => (
                              <MenuItem key={role.id} value={role.id}>
                                {role.display_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? t('common.active') : t('common.inactive')}
                        color={user.is_active ? 'success' : 'error'}
                        variant="filled"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenEdit(user)}
                        >
                          {t('common.edit')}
                        </Button>
                        {user.is_active ? (
                          <Button
                            size="small"
                            color="warning"
                            variant="outlined"
                            disabled={user.id === currentUser?.id}
                            onClick={() => handleDeactivateUser(user.id)}
                          >
                            {t('userManagement.deactivate')}
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            color="success"
                            variant="outlined"
                            onClick={() => handleActivateUser(user.id)}
                          >
                            {t('userManagement.activate')}
                          </Button>
                        )}
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<DeleteIcon />}
                          disabled={user.id === currentUser?.id}
                          onClick={() => setDeleteConfirmUser(user)}
                        >
                          {t('common.delete')}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan="6" sx={{ textAlign: 'center', py: 3 }}>
                    <Typography color="textSecondary">{t('userManagement.noUsers')}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </TableContainer>
      )}
      <Dialog open={!!deleteConfirmUser} onClose={() => setDeleteConfirmUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#d32f2f', color: 'white', fontWeight: 'bold' }}>
          {t('userManagement.deleteUserTitle')}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>
            {t('userManagement.deleteUserConfirm', { username: deleteConfirmUser?.username })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button onClick={() => setDeleteConfirmUser(null)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleDeleteUser}
            variant="contained"
            color="error"
            disabled={loading}
            startIcon={<DeleteIcon />}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 'bold' }}>
          {t('userManagement.createUserTitle')}
        </DialogTitle>
        <DialogContent sx={{ mt: 2, pb: 3 }}>
          <TextField
            fullWidth
            label={t('register.username')}
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            margin="normal"
            required
            size="small"
            autoComplete="off"
          />
          <TextField
            fullWidth
            label={t('register.email')}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            margin="normal"
            required
            size="small"
            autoComplete="off"
          />
          <TextField
            fullWidth
            label={t('register.password')}
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange}
            margin="normal"
            required
            size="small"
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label={t('register.firstName')}
            name="first_name"
            value={formData.first_name}
            onChange={handleInputChange}
            margin="normal"
            size="small"
          />
          <TextField
            fullWidth
            label={t('register.lastName')}
            name="last_name"
            value={formData.last_name}
            onChange={handleInputChange}
            margin="normal"
            size="small"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('userManagement.role')}</InputLabel>
            <Select
              name="role_id"
              value={formData.role_id}
              onChange={handleInputChange}
              label={t('userManagement.role')}
              size="small"
            >
              <MenuItem value="">
                <em>{t('userManagement.selectRole')}</em>
              </MenuItem>
              {roles.map(role => (
                <MenuItem key={role.id} value={role.id}>
                  {role.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button onClick={() => setShowModal(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            color="primary"
            disabled={loading || !formData.username.trim()}
          >
            {t('userManagement.createUser')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 'bold' }}>
          {t('userManagement.editUserTitle')}
        </DialogTitle>
        <DialogContent sx={{ mt: 2, pb: 3 }}>
          <TextField
            fullWidth
            label={t('register.username')}
            value={editFormData.username}
            margin="normal"
            size="small"
            disabled
          />
          <TextField
            fullWidth
            label={t('register.email')}
            name="email"
            type="email"
            value={editFormData.email}
            onChange={handleEditInputChange}
            margin="normal"
            required
            size="small"
            autoComplete="off"
          />
          <TextField
            fullWidth
            label={t('register.firstName')}
            name="first_name"
            value={editFormData.first_name}
            onChange={handleEditInputChange}
            margin="normal"
            size="small"
          />
          <TextField
            fullWidth
            label={t('register.lastName')}
            name="last_name"
            value={editFormData.last_name}
            onChange={handleEditInputChange}
            margin="normal"
            size="small"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('userManagement.role')}</InputLabel>
            <Select
              name="role_id"
              value={editFormData.role_id}
              onChange={handleEditInputChange}
              label={t('userManagement.role')}
              size="small"
            >
              <MenuItem value="">
                <em>{t('userManagement.selectRole')}</em>
              </MenuItem>
              {roles.map(role => (
                <MenuItem key={role.id} value={role.id}>
                  {role.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button onClick={() => setEditModalOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleUpdateUser}
            variant="contained"
            color="primary"
            disabled={loading || !editFormData.email.trim()}
          >
            {t('userManagement.updateUser')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
